import { Column, OnChangeFn, VisibilityState } from '@tanstack/react-table';
import { useReportBlockConfig } from '@va/ui/components/report-block';
import React, { PropsWithChildren, createContext, useContext, useMemo, useRef } from 'react';
import { useColumnVisibility } from './useColumnVisibility';

export type ColumnVisibilityColumn = {
  id: string;
  label: string;
  canHide: boolean;
};

type ColumnVisibilityContextValue = {
  isProvided: boolean;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  isNonDefault: boolean;
  resetToDefault: () => void;
  columnsRef: React.RefObject<Column<any, unknown>[]>;
};

const defaultRef = { current: [] };

const ColumnVisibilityCtx = createContext<ColumnVisibilityContextValue>({
  isProvided: false,
  columnVisibility: {},
  onColumnVisibilityChange: () => {},
  isNonDefault: false,
  resetToDefault: () => {},
  columnsRef: defaultRef,
});

type ColumnVisibilityProviderProps = PropsWithChildren<{
  reportBlockId: string;
  selectedView: string;
}>;

export const ColumnVisibilityProvider = ({ reportBlockId, selectedView, children }: ColumnVisibilityProviderProps) => {
  const { storageNamespace } = useReportBlockConfig();

  const { columnVisibility, onColumnVisibilityChange, isNonDefault, resetToDefault } = useColumnVisibility({
    namespace: storageNamespace,
    reportBlockId,
    selectedView,
  });

  const columnsRef = useRef<Column<any, unknown>[]>([]);

  const value = useMemo<ColumnVisibilityContextValue>(
    () => ({ isProvided: true, columnVisibility, onColumnVisibilityChange, isNonDefault, resetToDefault, columnsRef }),
    [columnVisibility, onColumnVisibilityChange, isNonDefault, resetToDefault],
  );

  return <ColumnVisibilityCtx.Provider value={value}>{children}</ColumnVisibilityCtx.Provider>;
};

export const useColumnVisibilityContext = () => useContext(ColumnVisibilityCtx);
